import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart, Bar, ResponsiveContainer, Tooltip, XAxis, YAxis, PieChart, Pie, Cell, LineChart, Line } from "recharts";
import { ArrowUpRight, AlertTriangle } from "lucide-react";
import { api } from "@/lib/apiClient";

const COLORS = ["#FFC93C", "#FF4F5A", "#2B2B2B", "#E6DDD3", "#A07F58", "#5B3924"];

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setData(r.data)); }, []);

  if (!data) return <div data-testid="admin-dash-loading" className="text-brand-charcoal/60">Loading dashboard…</div>;

  const counts = data.counts || {};
  const tiles = [
    { label: "Stock Requests", k: "stock_requests", to: "/admin/demand" },
    { label: "Inventions", k: "dream_pops", to: "/admin/submissions" },
    { label: "Reviews", k: "reviews", to: "/admin/reviews" },
    { label: "Franchise Leads", k: "franchise_leads", to: "/admin/leads" },
    { label: "Event Orders", k: "event_orders", to: "/admin/leads" },
    { label: "Bulk Orders", k: "bulk_orders", to: "/admin/leads" },
    { label: "Student Apps", k: "student_applications", to: "/admin/leads" },
    { label: "CRM Contacts", k: "crm", to: "/admin/crm" },
  ];

  return (
    <div data-testid="admin-dashboard" className="space-y-10">
      <header>
        <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Overview</div>
        <h1 className="editorial-h1 text-5xl mt-2">Pop Panda command room.</h1>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {tiles.map((t) => (
          <Link key={t.k} to={t.to} data-testid={`tile-${t.k}`} className="card-elevated p-5 group">
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55 flex items-center justify-between">{t.label}<ArrowUpRight size={14} className="text-brand-charcoal/40 group-hover:text-brand-red" /></div>
            <div className="font-display text-4xl mt-2">{counts[t.k] || 0}</div>
          </Link>
        ))}
      </section>

      <section className="grid lg:grid-cols-3 gap-6">
        <div className="card-elevated p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4"><div className="font-display text-2xl">Invention submissions (14 days)</div></div>
          <div className="h-64">
            <ResponsiveContainer>
              <LineChart data={data.submissions_trend || []}>
                <XAxis dataKey="day" stroke="#736F6A" fontSize={10} />
                <YAxis stroke="#736F6A" fontSize={10} allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#FF4F5A" strokeWidth={2} dot={{ fill: "#FFC93C" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-elevated p-6">
          <div className="font-display text-2xl mb-4">Top Flavours</div>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={(data.top_flavors || []).map((f) => ({ name: f._id, count: f.count }))} layout="vertical">
                <XAxis type="number" stroke="#736F6A" fontSize={10} />
                <YAxis dataKey="name" type="category" width={110} stroke="#736F6A" fontSize={10} />
                <Tooltip />
                <Bar dataKey="count" fill="#FFC93C" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
            {!data.top_flavors?.length && <div className="text-sm text-brand-charcoal/60 text-center mt-8">No Dream Pop submissions yet.</div>}
          </div>
        </div>
      </section>

      <section className="grid lg:grid-cols-3 gap-6">
        <div className="card-elevated p-6">
          <div className="font-display text-2xl mb-4">Dream Pops by Category</div>
          <div className="h-60">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={(data.dream_pops_by_category || []).map((d) => ({ name: d._id, value: d.count }))} dataKey="value" innerRadius={40} outerRadius={80}>
                  {(data.dream_pops_by_category || []).map((_, i) => (<Cell key={i} fill={COLORS[i % COLORS.length]} />))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            {!data.dream_pops_by_category?.length && <div className="text-sm text-brand-charcoal/60 text-center -mt-32">No submissions yet.</div>}
          </div>
        </div>

        <div className="card-elevated p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display text-2xl">Low stock alerts</div>
            <Link to="/admin/inventory" className="btn-pill btn-ghost text-xs">Open inventory <ArrowUpRight size={14} /></Link>
          </div>
          <div className="space-y-2">
            {(data.low_stock_alerts || []).length === 0 && <div className="text-sm text-brand-charcoal/60">All stocks above reorder levels. 🎉</div>}
            {(data.low_stock_alerts || []).map((l) => (
              <div key={l.id} className="flex items-center justify-between border border-brand-line rounded-2xl p-3">
                <div className="flex items-center gap-3"><AlertTriangle size={16} className="text-brand-red" /><div className="text-sm font-medium">{l.name}</div></div>
                <div className="text-xs text-brand-charcoal/65">{l.quantity} {l.unit} left · reorder at {l.reorder_level}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
